import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { ArrowLeft, Monitor, Cpu, Camera, Battery, Weight, X, Plus, Smartphone, Wifi, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { allIPhoneModels, getAllYears, type IPhoneModel } from "@/data/allIPhoneModels";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/hooks/useTheme";

// Local iPhone images
import iphone17ProMaxImg from "@/assets/phones/iphone-17-pro-max.jpg";
import iphone17ProImg from "@/assets/phones/iphone-17-pro.jpg";
import iphone17Img from "@/assets/phones/iphone-17.jpg";
import iphone17AirImg from "@/assets/phones/iphone-17-air.jpg";
import iphone15PlusImg from "@/assets/phones/iphone-15-plus.jpg";
import iphone8Img from "@/assets/phones/iphone-8.jpg";
import iphone7Img from "@/assets/phones/iphone-7.jpg";
import iphone6Img from "@/assets/phones/iphone-6.jpg";
import iphone5Img from "@/assets/phones/iphone-5.jpg";
import iphone4Img from "@/assets/phones/iphone-4.jpg";
import iphone3gImg from "@/assets/phones/iphone-3g.jpg";
import iphoneOriginalImg from "@/assets/phones/iphone-original.jpg";

// Map model IDs to local images (for models with placeholder strings or missing images)
const localImageMap: Record<string, string> = {
  // iPhone 17 series
  "iphone-17-pro-max": iphone17ProMaxImg,
  "iphone-17-pro": iphone17ProImg,
  "iphone-17-plus": iphone17Img,
  "iphone-17": iphone17Img,
  "iphone-17-air": iphone17AirImg,
  // iPhone 15 Plus
  "iphone-15-plus": iphone15PlusImg,
  // iPhone 8 and similar era
  "iphone-8-plus": iphone8Img,
  "iphone-8": iphone8Img,
  // iPhone 7
  "iphone-7-plus": iphone7Img,
  "iphone-7": iphone7Img,
  // iPhone SE 1st gen
  "iphone-se-1": iphone5Img,
  // iPhone 6s/6
  "iphone-6s-plus": iphone6Img,
  "iphone-6s": iphone6Img,
  "iphone-6-plus": iphone6Img,
  "iphone-6": iphone6Img,
  // iPhone 5s/5c/5
  "iphone-5s": iphone5Img,
  "iphone-5c": iphone5Img,
  "iphone-5": iphone5Img,
  // iPhone 4/4S
  "iphone-4s": iphone4Img,
  "iphone-4": iphone4Img,
  // iPhone 3G/3GS
  "iphone-3gs": iphone3gImg,
  "iphone-3g": iphone3gImg,
  // Original
  "iphone-original": iphoneOriginalImg,
};

const resolveImageStatic = (modelId: string, image?: string, dbImages?: Record<string, string>) => {
  if (dbImages?.[modelId]) return dbImages[modelId];
  if (localImageMap[modelId]) return localImageMap[modelId];
  if (image && !image.startsWith("__")) return image;
  return undefined;
};

interface IPhoneComparisonStandaloneProps {
  onBack: () => void;
}

interface SpecRow {
  icon: React.ReactNode;
  label: string;
  category: string;
  getValue: (model: IPhoneModel | null) => string;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 300,
      damping: 24,
    },
  },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, scale: 0.9, rotateY: -10 },
  visible: {
    opacity: 1,
    scale: 1,
    rotateY: 0,
    transition: {
      type: "spring" as const,
      stiffness: 200,
      damping: 20,
    },
  },
  hover: {
    scale: 1.02,
    y: -5,
    transition: {
      type: "spring" as const,
      stiffness: 400,
      damping: 20,
    },
  },
};

const shimmerVariants: Variants = {
  hidden: { x: "-100%" },
  visible: {
    x: "100%",
    transition: {
      repeat: Infinity,
      duration: 2,
      ease: "linear" as const,
    },
  },
};

export const IPhoneComparisonStandalone = ({ onBack }: IPhoneComparisonStandaloneProps) => {
  const [selectedIds, setSelectedIds] = useState<(string | null)[]>([null, null, null]);
  const [dbImages, setDbImages] = useState<Record<string, string>>({});
  const { preference } = useTheme();

  // Determine if dark mode is active
  const isDark = preference === "dark" || (preference === "auto" && (new Date().getHours() < 6 || new Date().getHours() >= 18));

  // Fetch custom images from DB
  useEffect(() => {
    const fetchCustomImages = async () => {
      const { data } = await supabase.from("iphone_custom_images").select("model_id, image_url");
      if (data) {
        const map: Record<string, string> = {};
        data.forEach(row => { map[row.model_id] = row.image_url; });
        setDbImages(map);
      }
    };
    fetchCustomImages();
  }, []);

  const resolveImage = (modelId: string, image?: string) =>
    resolveImageStatic(modelId, image, dbImages);

  const years = useMemo(() => getAllYears(), []);

  // Group models by year for the dropdown
  const modelsByYear = useMemo(() => {
    const grouped: Record<number, IPhoneModel[]> = {};
    allIPhoneModels.forEach((model) => {
      if (!grouped[model.year]) grouped[model.year] = [];
      grouped[model.year].push(model);
    });
    return grouped;
  }, []);

  const selectedModels = useMemo(() => {
    return selectedIds.map((id) =>
      id ? allIPhoneModels.find((m) => m.id === id) || null : null
    );
  }, [selectedIds]);

  const handleSelectModel = (index: number, modelId: string | null) => {
    const newIds = [...selectedIds];
    newIds[index] = modelId;
    setSelectedIds(newIds);
  };

  const clearSlot = (index: number) => {
    handleSelectModel(index, null);
  };

  const getSpecValue = (model: IPhoneModel | null, key: keyof IPhoneModel["specs"]): string => {
    if (!model) return "—";
    const value = model.specs[key];
    if (value === null || value === undefined || value === "") return "N/D";
    if (typeof value === "boolean") return value ? "Sì" : "No";
    return String(value);
  };

  const specRows: SpecRow[] = [
    // Display Section
    { icon: <Monitor className="w-4 h-4" />, label: "Dimensioni Display", category: "Display", getValue: (m) => getSpecValue(m, "display_size") },
    { icon: <Monitor className="w-4 h-4" />, label: "Tecnologia", category: "Display", getValue: (m) => getSpecValue(m, "display_technology") },
    { icon: <Monitor className="w-4 h-4" />, label: "Risoluzione", category: "Display", getValue: (m) => getSpecValue(m, "display_resolution") },
    { icon: <Monitor className="w-4 h-4" />, label: "Luminosità", category: "Display", getValue: (m) => getSpecValue(m, "display_brightness") },
    { icon: <Monitor className="w-4 h-4" />, label: "Refresh Rate", category: "Display", getValue: (m) => getSpecValue(m, "display_refresh_rate") },
    // Chip Section
    { icon: <Cpu className="w-4 h-4" />, label: "Processore", category: "Chip", getValue: (m) => getSpecValue(m, "chip_model") },
    { icon: <Cpu className="w-4 h-4" />, label: "Core CPU", category: "Chip", getValue: (m) => getSpecValue(m, "chip_cores") },
    { icon: <Cpu className="w-4 h-4" />, label: "GPU", category: "Chip", getValue: (m) => getSpecValue(m, "chip_gpu") },
    // Camera Section
    { icon: <Camera className="w-4 h-4" />, label: "Fotocamera Principale", category: "Fotocamera", getValue: (m) => getSpecValue(m, "camera_main") },
    { icon: <Camera className="w-4 h-4" />, label: "Ultra-grandangolare", category: "Fotocamera", getValue: (m) => getSpecValue(m, "camera_ultrawide") },
    { icon: <Camera className="w-4 h-4" />, label: "Teleobiettivo", category: "Fotocamera", getValue: (m) => getSpecValue(m, "camera_telephoto") },
    { icon: <Camera className="w-4 h-4" />, label: "Zoom Ottico", category: "Fotocamera", getValue: (m) => getSpecValue(m, "camera_zoom") },
    { icon: <Camera className="w-4 h-4" />, label: "Video", category: "Fotocamera", getValue: (m) => getSpecValue(m, "camera_video") },
    // Battery Section
    { icon: <Battery className="w-4 h-4" />, label: "Riproduzione Video", category: "Batteria", getValue: (m) => getSpecValue(m, "battery_video_playback") },
    { icon: <Battery className="w-4 h-4" />, label: "Ricarica", category: "Batteria", getValue: (m) => getSpecValue(m, "battery_charging") },
    // Dimensions Section
    { icon: <Weight className="w-4 h-4" />, label: "Peso", category: "Dimensioni", getValue: (m) => getSpecValue(m, "weight") },
    { icon: <Weight className="w-4 h-4" />, label: "Dimensioni", category: "Dimensioni", getValue: (m) => getSpecValue(m, "dimensions") },
    { icon: <Shield className="w-4 h-4" />, label: "Resistenza Acqua", category: "Dimensioni", getValue: (m) => getSpecValue(m, "water_resistance") },
    // Features Section
    { icon: <Smartphone className="w-4 h-4" />, label: "Face ID", category: "Funzioni", getValue: (m) => getSpecValue(m, "face_id") },
    { icon: <Smartphone className="w-4 h-4" />, label: "Touch ID", category: "Funzioni", getValue: (m) => getSpecValue(m, "touch_id") },
    { icon: <Wifi className="w-4 h-4" />, label: "5G", category: "Funzioni", getValue: (m) => getSpecValue(m, "connectivity_5g") },
  ];

  // Group rows by category
  const groupedRows = specRows.reduce((acc, row) => {
    if (!acc[row.category]) acc[row.category] = [];
    acc[row.category].push(row);
    return acc;
  }, {} as Record<string, SpecRow[]>);

  const hasAnySelection = selectedIds.some((id) => id !== null);

  // Theme-aware class helpers
  const mainBg = isDark
    ? "bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] text-white"
    : "bg-gradient-to-b from-gray-50 via-white to-gray-50 text-gray-900";

  const headerBg = isDark
    ? "bg-[#0a1628]/80 backdrop-blur-xl border-b border-white/10"
    : "bg-white/80 backdrop-blur-xl border-b border-gray-200";

  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-white/60" : "text-gray-500";
  const textMuted = isDark ? "text-white/80" : "text-gray-700";
  const textSpec = isDark ? "text-white/90" : "text-gray-800";
  const textSpecLabel = isDark ? "text-white/60" : "text-gray-500";
  const textPlaceholder = isDark ? "text-white/30" : "text-gray-300";
  const textPlaceholderName = isDark ? "text-white/50" : "text-gray-400";

  const selectTriggerClass = isDark
    ? "bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-teal-500/50"
    : "bg-gray-100 border-gray-300 text-gray-900 hover:bg-gray-200 hover:border-teal-500/50";

  const selectContentClass = isDark
    ? "bg-[#1a2a42] border-white/20 text-white"
    : "bg-white border-gray-200 text-gray-900";

  const selectItemClass = isDark
    ? "text-white focus:bg-teal-600/30 focus:text-white"
    : "text-gray-900 focus:bg-teal-100 focus:text-gray-900";

  const selectYearHeader = isDark
    ? "text-teal-400 bg-white/5"
    : "text-teal-600 bg-gray-100";

  const cardBg = isDark
    ? "bg-gradient-to-b from-white/10 to-white/5 border-white/10"
    : "bg-gradient-to-b from-gray-100 to-gray-50 border-gray-200";

  const specCardBg = isDark
    ? "bg-white/5 border-white/10"
    : "bg-white border-gray-200 shadow-sm";

  const specRowBorder = isDark ? "border-white/5" : "border-gray-100";

  const categoryIconBg = isDark
    ? "bg-gradient-to-br from-teal-500/30 to-teal-600/20 text-teal-400"
    : "bg-gradient-to-br from-teal-100 to-teal-50 text-teal-600";

  const categoryText = isDark ? "text-teal-400" : "text-teal-600";

  const yearBadgeBg = isDark ? "bg-black/60" : "bg-white/80 shadow-sm";

  const emptyStateBg = isDark
    ? "bg-gradient-to-br from-teal-500/30 to-blue-500/20"
    : "bg-gradient-to-br from-teal-100 to-blue-50";

  const placeholderCardBg = isDark
    ? "bg-gradient-to-b from-white/5 to-transparent"
    : "bg-gradient-to-b from-gray-100 to-transparent";

  const fallbackBg = isDark
    ? "bg-gradient-to-b from-teal-500/20 to-transparent"
    : "bg-gradient-to-b from-teal-100 to-transparent";

  const btnGhostClass = isDark
    ? "text-white/80 hover:text-white hover:bg-white/10"
    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100";

  const animBlobA = isDark ? "bg-teal-500/10" : "bg-teal-300/10";
  const animBlobB = isDark ? "bg-blue-500/10" : "bg-blue-300/10";

  return (
    <div className={`min-h-screen pb-24 ${mainBg}`}>
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className={`absolute top-1/4 -left-32 w-64 h-64 ${animBlobA} rounded-full blur-3xl`}
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute bottom-1/4 -right-32 w-96 h-96 ${animBlobB} rounded-full blur-3xl`}
          animate={{
            x: [0, -50, 0],
            y: [0, -30, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Header */}
      <motion.header 
        className={`sticky top-0 z-30 ${headerBg}`}
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className={btnGhostClass}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </motion.div>
            <div>
              <motion.h1 
                className={`text-xl font-semibold ${textPrimary} flex items-center gap-2`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                Confronta iPhone
                <motion.span
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className={`w-5 h-5 ${categoryText}`} />
                </motion.span>
              </motion.h1>
              <motion.p 
                className={`text-sm ${textSecondary}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                Tutti i {allIPhoneModels.length} modelli dal 2007 ad oggi
              </motion.p>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Selection Slots */}
      <motion.div 
        className={`sticky top-[73px] z-20 ${headerBg}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <motion.div 
            className="grid grid-cols-3 gap-3"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {[0, 1, 2].map((index) => {
              const model = selectedModels[index];
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="flex flex-col"
                >
                  <div className="relative">
                    <Select
                      value={selectedIds[index] || ""}
                      onValueChange={(value) => handleSelectModel(index, value || null)}
                    >
                      <SelectTrigger className={`w-full text-xs h-10 focus:ring-teal-500 transition-all duration-300 ${selectTriggerClass}`}>
                        <SelectValue placeholder={`iPhone ${index + 1}`} />
                      </SelectTrigger>
                      <SelectContent className={`max-h-[400px] ${selectContentClass}`}>
                        {years.map((year) => (
                          <div key={year}>
                            <div className={`px-2 py-1.5 text-xs font-semibold ${selectYearHeader} sticky top-0`}>
                              {year}
                            </div>
                            {modelsByYear[year]?.map((m) => (
                              <SelectItem
                                key={m.id}
                                value={m.id}
                                className={selectItemClass}
                                disabled={selectedIds.includes(m.id) && selectedIds[index] !== m.id}
                              >
                                {m.name}
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                    <AnimatePresence>
                      {model && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0, rotate: -180 }}
                          animate={{ scale: 1, opacity: 1, rotate: 0 }}
                          exit={{ scale: 0, opacity: 0, rotate: 180 }}
                          transition={{ type: "spring", stiffness: 400, damping: 20 }}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500/80 hover:bg-red-500 text-white shadow-lg shadow-red-500/30"
                            onClick={() => clearSlot(index)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Model Image Card */}
                  <motion.div
                    className={`mt-3 rounded-xl overflow-hidden border relative group ${cardBg}`}
                    variants={cardVariants}
                    whileHover="hover"
                  >
                    {/* Shimmer effect */}
                    <motion.div
                      className={`absolute inset-0 bg-gradient-to-r from-transparent ${isDark ? "via-white/10" : "via-gray-200/40"} to-transparent`}
                      variants={shimmerVariants}
                      initial="hidden"
                      animate={model ? "visible" : "hidden"}
                      style={{ width: "200%" }}
                    />
                    
                    <AspectRatio ratio={1}>
                      <AnimatePresence mode="wait">
                        {model ? (
                          <motion.div
                            key={model.id}
                            className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
                            initial={{ opacity: 0, scale: 0.8, rotateY: 90 }}
                            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                            exit={{ opacity: 0, scale: 0.8, rotateY: -90 }}
                            transition={{ 
                              type: "spring", 
                              stiffness: 200, 
                              damping: 20,
                              duration: 0.5 
                            }}
                          >
                            {resolveImage(model.id, model.image) ? (
                              <motion.img
                                src={resolveImage(model.id, model.image)}
                                alt={model.name}
                                className="w-full h-full object-cover"
                                initial={{ scale: 1.2 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5 }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.nextElementSibling;
                                  if (fallback) (fallback as HTMLElement).style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-full h-full flex-col items-center justify-center p-3 ${fallbackBg} absolute inset-0`}
                              style={{ display: resolveImage(model.id, model.image) ? 'none' : 'flex' }}
                            >
                              <motion.div
                                animate={{ 
                                  y: [0, -5, 0],
                                  rotateZ: [0, 5, -5, 0]
                                }}
                                transition={{ duration: 3, repeat: Infinity }}
                              >
                                <Smartphone className={`w-12 h-12 ${categoryText}`} />
                              </motion.div>
                              <span className={`text-xs mt-2 ${textSecondary}`}>{model.name}</span>
                            </div>
                            {/* Year badge */}
                            <motion.div
                              className={`absolute bottom-2 right-2 px-2 py-1 ${yearBadgeBg} backdrop-blur-sm rounded-full`}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                            >
                              <span className={`text-[10px] ${categoryText} font-medium`}>{model.year}</span>
                            </motion.div>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="placeholder"
                            className={`w-full h-full flex items-center justify-center ${textPlaceholder} ${placeholderCardBg}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <motion.div
                              animate={{ scale: [1, 1.1, 1] }}
                              transition={{ duration: 2, repeat: Infinity }}
                            >
                              <Plus className="w-8 h-8" />
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </AspectRatio>
                  </motion.div>

                  {/* Model Name */}
                  <motion.p className={`mt-2 text-center text-xs font-medium ${textMuted} truncate`} layout>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={model?.name || "placeholder"}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className={model ? textPrimary : textPlaceholderName}
                      >
                        {model?.name || "Seleziona"}
                      </motion.span>
                    </AnimatePresence>
                  </motion.p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </motion.div>

      {/* Comparison Table */}
      <AnimatePresence>
        {hasAnySelection && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: "spring", stiffness: 200, damping: 25 }}
          >
            <ScrollArea className="flex-1">
              <div className="max-w-7xl mx-auto px-4 py-6">
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {Object.entries(groupedRows).map(([category, rows], catIndex) => (
                    <motion.div
                      key={category}
                      variants={itemVariants}
                      className="mb-6"
                    >
                      {/* Category Header */}
                      <motion.div 
                        className="flex items-center gap-2 mb-3"
                        whileHover={{ x: 5 }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        <motion.div 
                          className={`p-2 rounded-lg ${categoryIconBg}`}
                          whileHover={{ scale: 1.1, rotate: 10 }}
                          transition={{ type: "spring", stiffness: 400, damping: 15 }}
                        >
                          {rows[0].icon}
                        </motion.div>
                        <h3 className={`text-sm font-semibold ${categoryText} uppercase tracking-wider`}>{category}</h3>
                      </motion.div>

                      {/* Specs Rows */}
                      <motion.div 
                        className={`rounded-xl overflow-hidden backdrop-blur-sm ${specCardBg}`}
                        whileHover={{ borderColor: "rgba(45, 212, 191, 0.3)" }}
                        transition={{ duration: 0.3 }}
                      >
                        {rows.map((row, rowIndex) => (
                          <motion.div
                            key={row.label}
                            className={`grid grid-cols-4 gap-2 p-3 ${
                              rowIndex !== rows.length - 1 ? `border-b ${specRowBorder}` : ""
                            }`}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: catIndex * 0.05 + rowIndex * 0.02 }}
                            whileHover={{ backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)" }}
                          >
                            <div className={`text-xs ${textSpecLabel} font-medium`}>{row.label}</div>
                            {selectedModels.map((model, colIndex) => (
                              <motion.div 
                                key={colIndex} 
                                className={`text-xs ${textSpec} text-center`}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: colIndex * 0.1 }}
                              >
                                {row.getValue(model)}
                              </motion.div>
                            ))}
                          </motion.div>
                        ))}
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      <AnimatePresence>
        {!hasAnySelection && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="flex flex-col items-center justify-center py-20 px-4"
          >
            <motion.div 
              className={`w-24 h-24 rounded-full ${emptyStateBg} flex items-center justify-center mb-6 relative`}
              animate={{ 
                boxShadow: isDark ? [
                  "0 0 20px rgba(45, 212, 191, 0.2)",
                  "0 0 40px rgba(45, 212, 191, 0.4)",
                  "0 0 20px rgba(45, 212, 191, 0.2)",
                ] : [
                  "0 0 20px rgba(45, 212, 191, 0.1)",
                  "0 0 30px rgba(45, 212, 191, 0.2)",
                  "0 0 20px rgba(45, 212, 191, 0.1)",
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <motion.div
                animate={{ 
                  rotateY: [0, 360],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                <Smartphone className={`w-12 h-12 ${categoryText}`} />
              </motion.div>
            </motion.div>
            <motion.h3 
              className={`text-xl font-medium ${textPrimary} mb-2`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Inizia il confronto
            </motion.h3>
            <motion.p 
              className={`text-sm ${textSecondary} text-center max-w-xs`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Seleziona fino a 3 modelli di iPhone per confrontare le specifiche tecniche.
              Tutti i modelli dal 2007 ad oggi sono disponibili.
            </motion.p>
            
            {/* Animated arrows pointing to selectors */}
            <motion.div 
              className="mt-8 flex gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className={`w-3 h-3 rounded-full ${isDark ? "bg-teal-400/50" : "bg-teal-500/40"}`}
                  animate={{ 
                    y: [0, -10, 0],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
